import { type FC, useRef, useState } from "react";

import { clsx } from "clsx";

// The longest value a field takes (DraftingFieldValue::MOST).
const FIELD_VALUE_MAX = 200;

// A fill-in field's value, edited in place: a click opens it, Enter or leaving
// it saves, Esc puts it back. With none, `empty` says why: in italics when it's
// left for him, darker when it's still to come (`waiting`).
export const FieldValueEdit: FC<{
  label: string;
  value: string | null;
  empty: string | null;
  waiting: boolean;
  onSave(value: string): void;
}> = ({ label, value, empty, waiting, onSave }) => {
  const [editing, setEditing] = useState<string | null>(null);
  // Set once Enter or Esc closes it, so the blur its closing may fire saves nothing again.
  const closed = useRef(false);
  const open = () => {
    closed.current = false;
    setEditing(value ?? "");
  };
  const close = (save: boolean) => {
    if (closed.current || editing === null) return;
    closed.current = true;
    if (save && editing.trim() !== (value ?? "")) onSave(editing.trim());
    setEditing(null);
  };

  if (editing !== null) {
    return (
      <input
        aria-label={`${label} value`}
        // Opened by his click, so he types straight in.
        autoFocus
        value={editing}
        onChange={(event) => setEditing(event.target.value)}
        maxLength={FIELD_VALUE_MAX}
        onBlur={() => close(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter") close(true);
          if (event.key === "Escape") close(false);
        }}
        className="h-5 min-w-0 flex-1 rounded-none border-0 border-b border-slate-300 bg-transparent p-0 text-[12.5px] leading-5 text-slate-700 focus:border-slate-300 focus:shadow-none focus:ring-0 focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      aria-label={`Edit ${label}`}
      title={value ?? undefined}
      onClick={open}
      className={clsx(
        "min-w-0 truncate rounded text-left leading-5 hover:underline hover:decoration-slate-300 hover:underline-offset-2 focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:outline-none",
        value && "text-slate-700",
        !value && waiting && "text-slate-500",
        !value && !waiting && "text-slate-400 italic",
      )}
    >
      {value ?? empty}
    </button>
  );
};
